export function exportAuditJson({
  container,
  analysis,
  aiResult,
  sourceName,
  governance,
  comparison,
}) {
  const payload = {
    exportedAt: new Date().toISOString(),
    sourceName,
    container: container.metadata,
    counts: analysis.counts,
    ratings: analysis.ratings,
    inferredNamingRules: analysis.inferredRules,
    activeNamingRules: analysis.namingRules,
    findings: analysis.findings,
    governance: governance || null,
    comparison: comparison || null,
    aiReview: aiResult?.review || null,
    privacy:
      "The original GTM JSON and all AI provider API keys are intentionally excluded from this report.",
  };
  downloadBlob(
    new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }),
    `${safeStem(sourceName)}-audit.json`,
  );
}

export function exportFindingsCsv({ analysis, sourceName }) {
  const rows = [
    ["severity", "section", "category", "item", "description"],
    ...analysis.findings.map((item) => [
      item.severity,
      item.tabId,
      item.category,
      item.item,
      item.description,
    ]),
  ];
  const csv = rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
  downloadBlob(
    new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }),
    `${safeStem(sourceName)}-findings.csv`,
  );
}

export function exportWordReport({
  container,
  analysis,
  aiResult,
  sourceName,
  governance,
  comparison,
}) {
  const bytes = buildDocx({
    container,
    analysis,
    aiResult,
    sourceName,
    governance,
    comparison,
  });
  downloadBlob(
    new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    }),
    `${safeStem(sourceName)}-audit.docx`,
  );
}

export function buildDocx({
  container,
  analysis,
  aiResult,
  sourceName,
  governance,
  comparison,
}) {
  const counts = analysis.ratings.overall.counts;
  const generated = new Date();
  const body = [
    paragraph("GTM Container Audit Report", "Title"),
    paragraph(`Source: ${sourceName || container.metadata.name}`, "Subtitle"),
    paragraph(`Generated: ${generated.toLocaleString()}`, "Subtitle"),
    paragraph("Executive rating", "Heading1"),
    table(
      ["Dimension", "Score", "Grade", "Assessment"],
      [
        [
          "Overall",
          `${analysis.ratings.overall.score} / 100`,
          analysis.ratings.overall.grade,
          analysis.ratings.overall.label,
        ],
        [
          "Container health",
          `${analysis.ratings.health.score} / 100`,
          analysis.ratings.health.grade,
          analysis.ratings.health.label,
        ],
        [
          "Naming governance",
          `${analysis.ratings.naming.score} / 100`,
          analysis.ratings.naming.grade,
          analysis.ratings.naming.label,
        ],
        [
          "Measurement quality",
          analysis.ratings.measurement.score === null
            ? "Not assessed"
            : `${analysis.ratings.measurement.score} / 100`,
          analysis.ratings.measurement.grade,
          analysis.ratings.measurement.label,
        ],
      ],
    ),
    paragraph("Audit summary", "Heading1"),
    table(
      ["Metric", "Value"],
      [
        ["Errors", String(counts.error)],
        ["Warnings", String(counts.warning)],
        ["Information notes", String(counts.info)],
        ["Tags", String(analysis.counts.tags)],
        ["Triggers", String(analysis.counts.triggers)],
        ["Variables", String(analysis.counts.variables)],
        ["GA4 Event tags", String(analysis.counts.ga4Events)],
      ],
    ),
    paragraph("Automatically inferred naming policy", "Heading1"),
    table(
      ["Object", "Prefixes", "Separator", "Casing", "Confidence"],
      ["tag", "trigger", "variable"].map((type) => {
        const rule = analysis.namingRules[type];
        return [
          capitalize(type),
          rule.prefixes.join(", ") || "Not inferred",
          visibleSeparator(rule.separator),
          rule.casing,
          `${rule.confidence}%`,
        ];
      }),
    ),
  ];

  if (governance) {
    body.push(
      paragraph("Governance policy", "Heading1"),
      table(
        ["Control", "Result", "Actual", "Target"],
        governance.controls.map((item) => [
          item.name,
          item.passed ? "Pass" : "Fail",
          item.actual,
          item.target,
        ]),
      ),
      paragraph("Prioritized remediation backlog", "Heading1"),
      table(
        ["Priority", "Risk", "Count", "Owner", "Effort", "Recommended action"],
        governance.riskRegister.slice(0, 20).map((item) => [
          item.priority,
          item.category,
          String(item.count),
          item.owner,
          item.effort,
          item.recommendation,
        ]),
      ),
    );
  }

  if (comparison) {
    body.push(
      paragraph("Container change control", "Heading1"),
      table(
        ["Dimension", "Score delta"],
        Object.entries(comparison.scoreDelta).map(([key, value]) => [
          capitalize(key),
          value === null ? "Not assessed" : `${value > 0 ? "+" : ""}${value}`,
        ]),
      ),
      table(
        ["Change", "Object", "Baseline name", "Current name", "Detail"],
        comparison.changes.slice(0, 40).map((item) => [
          item.change,
          item.objectType,
          item.baselineName,
          item.currentName,
          item.detail,
        ]),
      ),
    );
  }

  ["health", "naming", "events"].forEach((tabId) => {
    const findings = analysis.findings.filter((item) => item.tabId === tabId);
    body.push(paragraph(sectionLabel(tabId), "Heading1"));
    if (!findings.length) {
      body.push(paragraph("No findings were recorded for this section.", "Body"));
    } else {
      body.push(
        table(
          ["Severity", "Category", "Item", "Description"],
          findings.map((item) => [
            capitalize(item.severity),
            item.category,
            item.item,
            item.description,
          ]),
        ),
      );
    }
  });

  if (aiResult?.review) {
    const review = aiResult.review;
    body.push(paragraph("Optional AI review", "Heading1"));
    body.push(paragraph(review.executive_summary, "Body"));
    body.push(
      paragraph(
        `Advisory naming score: ${review.naming_assessment?.suggested_score ?? "—"} / 100. ${review.naming_assessment?.observed_pattern || ""}`,
        "Body",
      ),
    );
    if (review.priority_actions?.length) {
      body.push(
        table(
          ["Priority", "Action", "Why", "Recommended fix"],
          review.priority_actions.map((item) => [
            capitalize(item.priority),
            item.title,
            item.why,
            item.fix,
          ]),
        ),
      );
    }
  }

  body.push(paragraph("Method and privacy note", "Heading1"));
  body.push(
    paragraph(
      "The deterministic audit parses the GTM export once and reuses one shared container model. The API key and original JSON are excluded from this document. Any AI review is advisory and is based on a sanitized summary rather than raw parameter values or custom HTML.",
      "Body",
    ),
  );

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${body.join("\n")}
    <w:sectPr>
      <w:pgSz w:w="11906" w:h="16838"/>
      <w:pgMar w:top="900" w:right="900" w:bottom="900" w:left="900"/>
    </w:sectPr>
  </w:body>
</w:document>`;

  const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="Aptos" w:hAnsi="Aptos"/><w:sz w:val="19"/><w:color w:val="181715"/></w:rPr></w:rPrDefault></w:docDefaults>
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/><w:pPr><w:spacing w:after="100"/></w:pPr></w:style>
  <w:style w:type="paragraph" w:styleId="Title"><w:name w:val="Title"/><w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:spacing w:after="180"/></w:pPr><w:rPr><w:rFonts w:ascii="Georgia" w:hAnsi="Georgia"/><w:color w:val="A52B24"/><w:sz w:val="38"/><w:b/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Subtitle"><w:name w:val="Subtitle"/><w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="45"/></w:pPr><w:rPr><w:color w:val="746D63"/><w:sz w:val="18"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="280" w:after="130"/></w:pPr><w:rPr><w:sz w:val="27"/><w:b/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Body"><w:name w:val="Body"/><w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="130" w:line="270" w:lineRule="auto"/></w:pPr></w:style>
</w:styles>`;

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
</Types>`;

  const rootRelationships = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
</Relationships>`;

  const documentRelationships = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

  const timestamp = generated.toISOString();
  const coreXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>GTM Container Audit Report</dc:title>
  <dc:creator>GTM Audit Atlas</dc:creator>
  <dcterms:created xsi:type="dcterms:W3CDTF">${timestamp}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${timestamp}</dcterms:modified>
</cp:coreProperties>`;

  return createStoredZip([
    { name: "[Content_Types].xml", data: contentTypes },
    { name: "_rels/.rels", data: rootRelationships },
    { name: "word/document.xml", data: documentXml },
    { name: "word/styles.xml", data: stylesXml },
    { name: "word/_rels/document.xml.rels", data: documentRelationships },
    { name: "docProps/core.xml", data: coreXml },
  ]);
}

function paragraph(text, style) {
  return `<w:p><w:pPr><w:pStyle w:val="${xml(style)}"/></w:pPr><w:r><w:t xml:space="preserve">${xml(text)}</w:t></w:r></w:p>`;
}

function table(headers, rows) {
  const widths = headers.map(() => Math.floor(9000 / headers.length));
  return `<w:tbl>
    <w:tblPr><w:tblW w:w="0" w:type="auto"/><w:tblBorders>
      <w:top w:val="single" w:sz="4" w:color="D8CEBF"/><w:left w:val="single" w:sz="4" w:color="D8CEBF"/>
      <w:bottom w:val="single" w:sz="4" w:color="D8CEBF"/><w:right w:val="single" w:sz="4" w:color="D8CEBF"/>
      <w:insideH w:val="single" w:sz="4" w:color="D8CEBF"/><w:insideV w:val="single" w:sz="4" w:color="D8CEBF"/>
    </w:tblBorders></w:tblPr>
    <w:tblGrid>${widths.map((width) => `<w:gridCol w:w="${width}"/>`).join("")}</w:tblGrid>
    ${row(headers, widths, true)}
    ${rows.map((values) => row(values, widths, false)).join("")}
  </w:tbl>`;
}

function row(cells, widths, header) {
  return `<w:tr>${cells
    .map(
      (cell, index) => `<w:tc><w:tcPr><w:tcW w:w="${widths[index]}" w:type="dxa"/>${
        header ? '<w:shd w:fill="F2E3DF"/>' : ""
      }</w:tcPr><w:p><w:r>${
        header ? '<w:rPr><w:b/><w:color w:val="A52B24"/></w:rPr>' : ""
      }<w:t xml:space="preserve">${xml(cell)}</w:t></w:r></w:p></w:tc>`,
    )
    .join("")}</w:tr>`;
}

function createStoredZip(files) {
  const encoder = new TextEncoder();
  const localChunks = [];
  const centralChunks = [];
  let offset = 0;
  const { time, date } = dosDateTime(new Date());

  files.forEach((file) => {
    const name = encoder.encode(file.name);
    const data = typeof file.data === "string" ? encoder.encode(file.data) : file.data;
    const checksum = crc32(data);
    const local = new Uint8Array(30 + name.length);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0x0800, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, time, true);
    localView.setUint16(12, date, true);
    localView.setUint32(14, checksum, true);
    localView.setUint32(18, data.length, true);
    localView.setUint32(22, data.length, true);
    localView.setUint16(26, name.length, true);
    local.set(name, 30);
    localChunks.push(local, data);

    const central = new Uint8Array(46 + name.length);
    const centralView = new DataView(central.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0x0800, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, time, true);
    centralView.setUint16(14, date, true);
    centralView.setUint32(16, checksum, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, name.length, true);
    centralView.setUint32(42, offset, true);
    central.set(name, 46);
    centralChunks.push(central);
    offset += local.length + data.length;
  });

  const centralSize = centralChunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const end = new Uint8Array(22);
  const view = new DataView(end.buffer);
  view.setUint32(0, 0x06054b50, true);
  view.setUint16(8, files.length, true);
  view.setUint16(10, files.length, true);
  view.setUint32(12, centralSize, true);
  view.setUint32(16, offset, true);
  return concatBytes([...localChunks, ...centralChunks, end]);
}

function crc32(bytes) {
  let checksum = 0xffffffff;
  for (const byte of bytes) {
    checksum = CRC_TABLE[(checksum ^ byte) & 0xff] ^ (checksum >>> 8);
  }
  return (checksum ^ 0xffffffff) >>> 0;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

function concatBytes(chunks) {
  const output = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
  let offset = 0;
  chunks.forEach((chunk) => {
    output.set(chunk, offset);
    offset += chunk.length;
  });
  return output;
}

function dosDateTime(value) {
  const year = Math.max(1980, value.getFullYear());
  return {
    time: (value.getHours() << 11) | (value.getMinutes() << 5) | Math.floor(value.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((value.getMonth() + 1) << 5) | value.getDate(),
  };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvCell(value) {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

function xml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function safeStem(value) {
  return (
    String(value || "gtm-container")
      .replace(/\.json$/i, "")
      .replace(/[^a-z0-9_-]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "gtm-container"
  );
}

function sectionLabel(value) {
  return { health: "Container Health", naming: "Naming Governance", events: "GA4 Events" }[value];
}

function visibleSeparator(value) {
  return value === " " ? "space" : String(value).replaceAll(" ", "·");
}

function capitalize(value) {
  return value ? value[0].toUpperCase() + value.slice(1) : "";
}
